import React from 'react';
import { createPortal } from 'react-dom';
import type { ToastType, UserProfile } from '../types';
import { resolveRoddoDarkMode } from '../constants/theme';
import ConfirmationModal from './ConfirmationModal';
import CrewDMConversationMenu from './CrewDMConversationMenu';
import { useRoutePortalTarget } from './layout/RoutePortalHost';
import {
  deleteCrewDMMessage,
  markCrewDMRead,
  sendCrewDMMessage,
  subscribeCrewDMMessages,
  subscribeCrewDMThreadState,
  subscribeCrewDMViewState,
  updateCrewDMMessage,
  type CrewDMMessage,
} from '../services/crewDMs';

const clean = (value: unknown) => String(value ?? '').trim();
const DEFAULT_AVATAR = '/avatar-default.png';

const fmtTime = (ms?: number) => {
  if (!ms) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(ms));
  } catch {
    return '';
  }
};

type Props = {
  dmId: string;
  targetUid: string;
  targetName: string;
  targetAvatar?: string;
  targetOnline?: boolean;
  currentUser: UserProfile;
  isDarkMode?: boolean;
  addToast: (message: string, type?: ToastType) => void;
  onBack: () => void;
  onOpenBoard?: () => void;
  onOpenProfile?: () => void;
  compact?: boolean;
};

export default function CrewDMView({
  dmId,
  targetUid,
  targetName,
  targetAvatar,
  targetOnline = false,
  currentUser,
  isDarkMode: explicitIsDarkMode,
  addToast,
  onBack,
  onOpenBoard,
  onOpenProfile,
  compact = false,
}: Props) {
  const portalTarget = useRoutePortalTarget();
  const uid = clean((currentUser as any)?.uid);
  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);
  const [messages, setMessages] = React.useState<CrewDMMessage[]>([]);
  const [threadExists, setThreadExists] = React.useState(false);
  const [threadStateReady, setThreadStateReady] = React.useState(false);
  const [clearedAt, setClearedAt] = React.useState(0);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [editingId, setEditingId] = React.useState('');
  const [editingText, setEditingText] = React.useState('');
  const [menuMessageId, setMenuMessageId] = React.useState('');
  const [messagePendingDelete, setMessagePendingDelete] = React.useState<CrewDMMessage | null>(null);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0 });
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const menuButtonRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  const messagesScrollRef = React.useRef<HTMLDivElement | null>(null);
  const bottomRef = React.useRef<HTMLDivElement | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const submitLockRef = React.useRef(false);

  const styles = React.useMemo(() => ({
    pageText: isDarkMode ? '#F2F6FC' : '#30271E',
    softText: isDarkMode ? '#A8B4C7' : '#6E6255',
    faintText: isDarkMode ? '#74839A' : '#8B7D6D',
    shell: isDarkMode ? '#0A1120' : '#FFF9F1',
    surface: isDarkMode ? '#101827' : '#FFFCF7',
    surfaceRaised: isDarkMode ? '#0F1828' : '#F7F2E8',
    input: isDarkMode ? '#0C1423' : '#FFFCF7',
    border: isDarkMode ? 'rgba(132,151,190,0.14)' : 'rgba(74,58,42,0.14)',
    borderStrong: isDarkMode ? 'rgba(132,151,190,0.22)' : 'rgba(74,58,42,0.20)',
    ownBubble: isDarkMode ? '#233251' : '#E9F2FF',
    theirBubble: isDarkMode ? '#121C2E' : '#F2EBE1',
    blue: isDarkMode ? '#8EA7FF' : '#2F80ED',
    green: '#34D399',
    danger: isDarkMode ? '#FF9AA5' : '#A63D49',
    menu: isDarkMode ? '#101827' : '#FFFCF7',
    shadow: isDarkMode ? '0 12px 26px rgba(0,0,0,.28)' : '0 12px 26px rgba(62,45,28,.12)',
  }), [isDarkMode]);

  React.useEffect(() => {
    setThreadStateReady(false);
    setThreadExists(false);

    if (!uid || !dmId) {
      setThreadStateReady(true);
      return;
    }

    return subscribeCrewDMThreadState(
      dmId,
      uid,
      (state) => {
        setThreadExists(Boolean(state?.exists));
        setThreadStateReady(true);
      },
      () => {
        setThreadExists(false);
        setThreadStateReady(true);
      },
    );
  }, [uid, dmId]);

  React.useEffect(() => {
    if (!uid || !dmId || !threadStateReady || !threadExists) {
      setClearedAt(0);
      return;
    }

    return subscribeCrewDMViewState(
      uid,
      dmId,
      (state) => setClearedAt(Number(state?.clearedAt || 0)),
      () => setClearedAt(0),
    );
  }, [uid, dmId, threadStateReady, threadExists]);

  React.useEffect(() => {
    if (!dmId || !threadStateReady || !threadExists) {
      setMessages([]);
      return;
    }

    return subscribeCrewDMMessages(
      dmId,
      (rows) => setMessages(rows.filter((row) => Number(row.createdAt || 0) > clearedAt)),
      () => addToast('Could not load Crew messages.', 'error'),
    );
  }, [dmId, addToast, clearedAt, threadStateReady, threadExists]);

  React.useEffect(() => {
    if (!uid || !dmId || !threadExists) return;
    void markCrewDMRead(uid, dmId);
  }, [uid, dmId, threadExists, messages.length]);

  React.useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      const scroller = messagesScrollRef.current;
      if (!scroller) return;
      scroller.scrollTop = scroller.scrollHeight;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [messages.length]);

  React.useEffect(() => {
    if (!menuMessageId) return;
    const close = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && menuRef.current?.contains(target)) return;
      const button = menuButtonRefs.current[menuMessageId];
      if (target && button?.contains(target)) return;
      setMenuMessageId('');
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuMessageId('');
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('touchstart', close, { passive: true });
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('touchstart', close);
      window.removeEventListener('keydown', escape);
    };
  }, [menuMessageId]);

  const send = async () => {
    const body = clean(text);
    if (!body || busy || submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      setBusy(true);
      await sendCrewDMMessage({ currentUser, targetUid, text: body });
      setThreadExists(true);
      setThreadStateReady(true);
      setText('');
      // Keep the composer active without asking WebKit to scroll the page again.
      window.requestAnimationFrame(() => {
        try {
          textareaRef.current?.focus({ preventScroll: true });
        } catch {
          textareaRef.current?.focus();
        }
      });
      void markCrewDMRead(uid, dmId);
    } catch (error: any) {
      console.error('[CrewDMView] send failed', error);
      addToast(clean(error?.message) || 'Could not send Crew message.', 'error');
    } finally {
      submitLockRef.current = false;
      setBusy(false);
    }
  };

  const startEdit = (message: CrewDMMessage) => {
    setMenuMessageId('');
    setEditingId(message.id);
    setEditingText(message.text || '');
  };

  const saveEdit = async () => {
    const body = clean(editingText);
    if (!editingId || !body || busy) return;
    try {
      setBusy(true);
      await updateCrewDMMessage(dmId, editingId, body);
      setEditingId('');
      setEditingText('');
      addToast('Message updated.', 'success');
    } catch (error: any) {
      addToast(clean(error?.message) || 'Could not update message.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const requestRemoveMessage = (message: CrewDMMessage) => {
    setMenuMessageId('');
    setMessagePendingDelete(message);
  };

  const confirmRemoveMessage = async () => {
    const message = messagePendingDelete;
    if (!message || busy) return;

    try {
      setBusy(true);
      await deleteCrewDMMessage(dmId, message.id);
      setMessagePendingDelete(null);
      addToast('Message removed.', 'success');
    } catch (error: any) {
      addToast(clean(error?.message) || 'Could not remove message.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const openMenu = (messageId: string) => {
    const button = menuButtonRefs.current[messageId];
    if (button) {
      const rect = button.getBoundingClientRect();
      setMenuPosition({ top: Math.round(rect.bottom + 6), left: Math.round(rect.right) });
    }
    setMenuMessageId((current) => current === messageId ? '' : messageId);
  };

  const avatar = clean(targetAvatar) || DEFAULT_AVATAR;

  return (
    <div
      data-roddo-dm-root="true"
      className={
        compact
          ? "flex h-[min(54vh,390px)] min-h-[320px] flex-col"
          : "flex min-h-[calc(100dvh-var(--roddo-header-h,72px)-var(--roddo-nav-h,72px)-36px)] flex-col"
      }
    >
      {!compact ? (
      <header
        className="sticky top-0 z-20 -mx-1 mb-2 flex items-center gap-3 border-b px-1 pb-3 pt-1 backdrop-blur-xl"
        style={{ background: `${styles.shell}F2`, borderColor: styles.border }}
      >
        <button
          type="button"
          onClick={onBack}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-2xl font-black"
          style={{ background: styles.surfaceRaised, color: styles.pageText }}
          aria-label="Back to Crew Dock"
        >
          ‹
        </button>

        <button
          type="button"
          onClick={onOpenProfile}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          disabled={!onOpenProfile}
        >
          <div className="relative h-11 w-11 shrink-0">
            <img
              src={avatar}
              alt=""
              className="h-11 w-11 rounded-full border object-cover"
              style={{ borderColor: styles.borderStrong }}
            />
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2"
              style={{ background: targetOnline ? styles.green : isDarkMode ? '#625C72' : '#A8A29E', borderColor: styles.shell }}
            />
          </div>
          <div className="min-w-0">
            <div className="truncate text-[16px] font-black" style={{ color: styles.pageText }}>
              {targetName || 'Crewmate'}
            </div>
            <div className="mt-0.5 text-[10px] font-black uppercase tracking-[0.12em]" style={{ color: targetOnline ? styles.green : styles.faintText }}>
              {targetOnline ? 'Online' : 'Crew message'}
            </div>
          </div>
        </button>

        {onOpenBoard && !compact ? (
          <button
            type="button"
            onClick={onOpenBoard}
            className="rounded-[10px] border px-3 py-2 text-[11px] font-black"
            style={{ background: styles.surfaceRaised, borderColor: styles.border, color: styles.softText }}
          >
            Board
          </button>
        ) : null}

        <CrewDMConversationMenu
          dmId={dmId}
          threadExists={threadExists}
          currentUid={uid}
          targetUid={targetUid}
          targetName={targetName}
          isDarkMode={isDarkMode}
          addToast={addToast}
          onDeleted={onBack}
          onBlocked={onBack}
        />
      </header>
      ) : null}

      <div
        ref={messagesScrollRef}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto px-0.5 pb-4 pt-2 roddo-content-scroll"
      >
        {!messages.length ? (
          <div
            className={`mx-auto max-w-sm rounded-[16px] border p-5 text-center ${compact ? "mt-5" : "mt-12"}`}
            style={{ background: styles.surface, borderColor: styles.border }}
          >
            <div className="text-2xl" aria-hidden="true">💬</div>
            <div className="mt-2 text-sm font-black" style={{ color: styles.pageText }}>
              Start a private Crew message
            </div>
            <div className="mt-1 text-xs font-semibold leading-5" style={{ color: styles.softText }}>
              Only you and {targetName || 'this crewmate'} can access this conversation while your Crew connection is accepted.
            </div>
          </div>
        ) : null}

        {messages.map((message) => {
          const mine = message.senderId === uid;
          const deleted = message.status === 'deleted';
          const editing = editingId === message.id;
          const edited = Boolean(message.editedAt && message.editedAt > message.createdAt + 500);

          return (
            <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`group relative max-w-[82%] ${mine ? 'pl-8' : 'pr-8'}`}>
                <div
                  className={`rounded-[16px] px-3.5 py-2.5 ${deleted ? "italic" : ""}`}
                  style={{
                    background: deleted
                      ? "transparent"
                      : mine
                        ? (isDarkMode ? "rgba(57,78,121,0.44)" : "rgba(47,128,237,0.09)")
                        : "transparent",
                    color: deleted
                      ? styles.faintText
                      : mine
                        ? styles.pageText
                        : styles.pageText,
                    boxShadow: mine && !deleted
                      ? (isDarkMode ? "0 3px 10px rgba(0,0,0,0.10)" : "0 2px 8px rgba(47,128,237,0.05)")
                      : "none",
                  }}
                >
                  {editing ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingText}
                        onChange={(event) => setEditingText(event.target.value)}
                        rows={3}
                        maxLength={2000}
                        autoFocus
                        className="w-full resize-none rounded-[10px] border px-3 py-2 text-sm font-semibold outline-none"
                        style={{ background: styles.input, borderColor: styles.border, color: styles.pageText }}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => { setEditingId(''); setEditingText(''); }}
                          className="rounded-[9px] border px-3 py-1.5 text-[11px] font-black"
                          style={{ borderColor: styles.border, color: styles.softText }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void saveEdit()}
                          disabled={!clean(editingText) || busy}
                          className="rounded-[9px] px-3 py-1.5 text-[11px] font-black text-white disabled:opacity-50"
                          style={{ background: styles.blue }}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className={`whitespace-pre-wrap break-words text-[13px] font-semibold leading-5 ${deleted ? 'opacity-60' : ''}`}
                        style={{
                          paddingLeft: !mine && !deleted ? 2 : 0,
                          paddingRight: !mine && !deleted ? 2 : 0,
                        }}
                      >
                        {deleted ? 'Message removed' : message.text}
                      </div>
                      <div
                        className={`mt-1.5 flex items-center gap-1.5 text-[9px] font-bold ${mine ? "justify-end" : "justify-start"}`}
                        style={{ color: styles.faintText }}
                      >
                        {edited && !deleted ? <span>edited</span> : null}
                        <span>{fmtTime(message.createdAt)}</span>
                      </div>
                    </>
                  )}
                </div>

                {mine && !deleted && !editing ? (
                  <button
                    ref={(node) => { menuButtonRefs.current[message.id] = node; }}
                    type="button"
                    onClick={() => openMenu(message.id)}
                    className="absolute left-0 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-lg font-black opacity-60 transition hover:opacity-100"
                    style={{ background: styles.surfaceRaised, color: styles.pageText }}
                    aria-label="Message options"
                  >
                    ⋯
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div
        data-roddo-keyboard-anchor="true"
        className="sticky bottom-0 z-20 -mx-1 border-t px-1 pb-1 pt-3 backdrop-blur-xl"
        style={{ background: `${styles.shell}F2`, borderColor: styles.border }}
      >
        <div
          className="flex items-end gap-2 rounded-[18px] p-2"
          style={{
            background: isDarkMode ? "rgba(16,24,39,0.96)" : "rgba(255,252,247,0.96)",
            boxShadow: isDarkMode
              ? "0 6px 18px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.035)"
              : "0 6px 18px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.70)",
          }}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            rows={1}
            maxLength={2000}
            placeholder={`Message ${targetName || 'crewmate'}...`}
            className="max-h-28 min-h-[42px] flex-1 resize-none bg-transparent px-2 py-2.5 text-sm font-semibold outline-none"
            style={{ color: styles.pageText }}
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={!clean(text) || busy}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-[19px] font-black text-white transition-[transform,box-shadow,filter] duration-150 hover:-translate-y-px active:translate-y-0 active:scale-95 disabled:translate-y-0 disabled:opacity-35"
            style={{
              background: isDarkMode ? "#F0C86D" : "#2F80ED",
              color: isDarkMode ? "#17202E" : "#FFFFFF",
              boxShadow: isDarkMode
                ? "0 6px 16px rgba(240,200,109,0.20)"
                : "0 6px 16px rgba(47,128,237,0.20)",
            }}
            aria-label="Send Crew message"
            title="Send"
          >
            <span aria-hidden="true" className="-translate-y-[1px]">➤</span>
          </button>
        </div>
        {!compact ? (
          <div className="mt-1.5 px-2 text-[9px] font-semibold" style={{ color: styles.faintText }}>
            Enter to send · Shift+Enter for a new line
          </div>
        ) : null}
      </div>

      {menuMessageId && portalTarget
        ? createPortal(
            <div
              ref={menuRef}
              data-roddo-quick-owned="true"
              className="fixed z-[99999] w-40 overflow-hidden rounded-[12px] border"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                transform: 'translateX(-100%)',
                background: styles.menu,
                borderColor: styles.border,
                boxShadow: styles.shadow,
              }}
            >
              {(() => {
                const message = messages.find((row) => row.id === menuMessageId);
                if (!message) return null;
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => startEdit(message)}
                      className="w-full px-4 py-3 text-left text-sm font-black hover:bg-black/5 dark:hover:bg-white/10"
                      style={{ color: styles.pageText }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => requestRemoveMessage(message)}
                      className="w-full px-4 py-3 text-left text-sm font-black hover:bg-black/5 dark:hover:bg-white/10"
                      style={{ color: styles.danger }}
                    >
                      Remove
                    </button>
                  </>
                );
              })()}
            </div>,
            portalTarget,
          )
        : null}

      <ConfirmationModal
        isOpen={!!messagePendingDelete}
        onClose={() => {
          if (!busy) setMessagePendingDelete(null);
        }}
        onConfirm={() => {
          void confirmRemoveMessage();
        }}
        title="Remove message?"
        message="This message will be removed from the private Crew conversation."
        confirmText={busy ? "Removing..." : "Remove"}
        cancelText="Cancel"
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
